import type {
  APIResponse,
  CvHealthApplyInput,
  CvHealthApplyResult,
  CvHealthDecisionInput,
  CvHealthUndoInput,
  CvHealthUndoResult,
  CvHealthLatestEnvelope,
  CvHealthReport,
  CvHealthReportEnvelope,
  CvHealthTargetKey,
  StartCvHealthReviewInput,
} from "@tabbio/types";
import { api } from "./api";

/**
 * CV Health client. Every function returns the raw `{ data, error, meta }`
 * envelope so callers can branch on the error code: `FEATURE_DISABLED` means
 * "skip the step, never a dead end". The latest endpoint answers
 * `{ report: null }` when no report exists yet; `NOT_FOUND` means the target
 * is not the caller's CV. `CV_HEALTH_REVIEW_FAILED` is a generic failure
 * (try again, or continue without checking).
 */

export const CV_HEALTH_FEATURE_DISABLED = "FEATURE_DISABLED";
export const CV_HEALTH_REVISION_CONFLICT = "CV_HEALTH_REVISION_CONFLICT";
/** The report (or CV) is not the caller's, or no longer exists: a re-run replaced it. */
export const CV_HEALTH_NOT_FOUND = "NOT_FOUND";

const BASE = "/api/cv-health";

/**
 * Start a review. `source: 'import'` (onboarding's first check) judges the
 * upload as parsed, never the current CV, and its report can never be
 * applied; `onboarding_recheck` reviews the current CV with the import
 * signals still honoured (the fix round reviews the written CV with it).
 */
export function startCvHealthReview(
  input: StartCvHealthReviewInput,
): Promise<APIResponse<CvHealthReportEnvelope>> {
  const { target, trigger, locale, importSignals, cvHealthToken, source, cvSelection } = input;
  return api.post<CvHealthReportEnvelope>(`${BASE}/reviews`, {
    target,
    trigger,
    locale,
    importSignals,
    cvHealthToken,
    source,
    ...(cvSelection ? { cvSelection } : {}),
  });
}

export function getCvHealthReview(
  reportId: string,
): Promise<APIResponse<CvHealthReportEnvelope>> {
  return api.get<CvHealthReportEnvelope>(
    `${BASE}/reviews/${encodeURIComponent(reportId)}`,
  );
}

export function getLatestCvHealthReport(
  target: CvHealthTargetKey,
): Promise<APIResponse<CvHealthLatestEnvelope>> {
  return api.get<CvHealthLatestEnvelope>(
    `${BASE}/targets/${encodeURIComponent(target)}/latest`,
  );
}

export function recordCvHealthDecision(
  input: CvHealthDecisionInput,
): Promise<APIResponse<{ report: CvHealthReport }>> {
  return api.post<{ report: CvHealthReport }>(
    `${BASE}/reviews/${encodeURIComponent(input.reportId)}/findings/${encodeURIComponent(input.findingKey)}/decision`,
    { decision: input.decision },
  );
}

export function isCvHealthFeatureDisabled<T>(
  response: APIResponse<T>,
): boolean {
  return response.error?.code === CV_HEALTH_FEATURE_DISABLED;
}

/** Apply one or more proposals server-side, then recheck. Returns the new report and a receipt for Undo. */
export function applyCvHealthChanges(
  input: CvHealthApplyInput,
): Promise<APIResponse<CvHealthApplyResult>> {
  return api.post<CvHealthApplyResult>(
    `${BASE}/reviews/${encodeURIComponent(input.reportId)}/apply`,
    { items: input.items, trigger: input.trigger },
  );
}

/** Restore the values in a receipt, reopen the findings, then recheck. */
export function undoCvHealthChanges(
  input: CvHealthUndoInput,
): Promise<APIResponse<CvHealthUndoResult>> {
  return api.post<CvHealthUndoResult>(
    `${BASE}/reviews/${encodeURIComponent(input.reportId)}/undo`,
    { receipt: input.receipt, trigger: input.trigger },
  );
}
